import useUpdateDocumentField from 'hooks/DocumentsRepository/useUpdateDocumentField';
import type {
  DocumentReferenceField,
  DocumentReferenceFieldTaxOther,
} from 'types/repository.types';
import useTaxLink from '../useTaxLink';
import useUpdateDocument from 'hooks/DocumentsRepository/useUpdateDocument';
import DocumentRepositoryFieldItem from './DocumentRepositoryFieldItem';
import { useTranslation } from 'react-i18next';
import formatPeriodLabel from 'utils/formatPeriodLabel';

import type { JSX } from 'react';

type DocumentRepositoryTaxFieldProps = {
  field: DocumentReferenceField & DocumentReferenceFieldTaxOther;
  documentId: string;
  setBoundingBoxField: (field: string | null) => void;
};

const DocumentRepositoryTaxField = ({
  field,
  documentId,
}: DocumentRepositoryTaxFieldProps): JSX.Element | null => {
  const { t } = useTranslation();

  const { mutate: updateDocument, isLoading: isUpdateDocumentLoading } =
    useUpdateDocument();
  const {
    mutate: updateDocumentField,
    isLoading: isUpdateDocumentFieldLoading,
  } = useUpdateDocumentField(documentId);
  const { taxId } = useTaxLink(field.value.period, field.value.type);

  const isLoading = isUpdateDocumentLoading || isUpdateDocumentFieldLoading;

  const handleAccept = () => {
    if (!taxId) return;

    updateDocument({ documentId, taxId });
    updateDocumentField({ ...field, status: 'accepted' });
  };

  const handleReject = () =>
    updateDocumentField({ ...field, status: 'rejected' });

  if (!taxId) return null;

  const textVariables = {
    taxType: t(`taxes.filters.types.${field.value.type}`),
    periodString: formatPeriodLabel(field.value.period),
  };

  return (
    <DocumentRepositoryFieldItem
      field={field}
      onAccept={handleAccept}
      onReject={handleReject}
      isLoading={isLoading}
      title={t(
        `repository.document_form.fields.${field.type}.title`,
        textVariables,
      )}
      description={t(
        `repository.document_form.fields.${field.type}.description`,
        textVariables,
      )}
      acceptButtonLabel={t('repository.document_form.fields.accept')}
      rejectButtonLabel={t('repository.document_form.fields.reject')}
    />
  );
};

export default DocumentRepositoryTaxField;
